import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, StatCard } from "./ui";
import { STATUS_META } from "@/lib/constants";
import { CalendarDays, Euro, CheckCircle2, XCircle, UserX, Users, Globe2, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { Loader2 } from "lucide-react";

export default function Dashboard() {
    const { t, lang } = useI18n();
    const { user, salon } = useAuth();
    const [range, setRange] = useState("week");
    const { data, isLoading } = useQuery({
        queryKey: ["dashboard", range],
        queryFn: async () => (await api.get(`/dashboard/stats?range=${range}`)).data,
    });
    const isPro = salon?.subscription?.status === "trial" || salon?.subscription?.plan === "professional";

    const ranges = [["today", t("common.today")], ["week", t("common.week")], ["month", t("common.month")]];

    return (
        <div>
            <PageHeader title={`${t("dashboard.welcome")}, ${user?.name?.split(" ")[0] || ""}`} subtitle={salon?.name}
                action={
                    <div className="flex gap-1 rounded-full border border-border/70 bg-card p-1">
                        {ranges.map(([k, label]) => (
                            <button key={k} onClick={() => setRange(k)} data-testid={`range-${k}`}
                                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${range === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                                {label}
                            </button>
                        ))}
                    </div>
                } />

            {isLoading || !data ? (
                <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>
            ) : (
                <>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <StatCard label={t("dashboard.todayAppointments")} value={data.today_count} icon={CalendarDays} />
                        <StatCard label={t("dashboard.revenue")} value={`€${data.revenue.toFixed(2)}`} icon={Euro} accent />
                        <StatCard label={t("dashboard.completed")} value={data.completed_count} icon={CheckCircle2} />
                        <StatCard label={t("dashboard.noShows")} value={data.no_show_count} icon={UserX} />
                        <StatCard label={t("dashboard.todayRevenue")} value={`€${data.today_revenue.toFixed(2)}`} icon={Euro} />
                        <StatCard label={t("dashboard.cancelled")} value={data.cancelled_count} icon={XCircle} />
                        <StatCard label={t("dashboard.customers")} value={data.customer_count} icon={Users} />
                        <StatCard label={t("dashboard.onlineBookings")} value={data.online_bookings} icon={Globe2} />
                    </div>

                    <div className="mt-6 grid gap-6 lg:grid-cols-3">
                        <div className="rounded-2xl border border-border/70 bg-card p-6 lg:col-span-2">
                            <h3 className="mb-4 flex items-center gap-2 font-semibold"><TrendingUp className="h-4 w-4 text-accent" />{t("dashboard.revPerEmployee")}</h3>
                            {isPro ? (
                                data.revenue_per_employee.length ? (
                                    <ResponsiveContainer width="100%" height={240}>
                                        <BarChart data={data.revenue_per_employee}>
                                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <Tooltip formatter={(v) => `€${v}`} />
                                            <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                                {data.revenue_per_employee.map((_, i) => <Cell key={i} fill={`hsl(var(--chart-${(i % 5) + 1}))`} />)}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : <p className="py-12 text-center text-sm text-muted-foreground">{t("common.noResults")}</p>
                            ) : (
                                <p className="py-12 text-center text-sm text-muted-foreground">{t("employees.limitInfo")}</p>
                            )}
                        </div>
                        <div className="rounded-2xl border border-border/70 bg-card p-6">
                            <h3 className="mb-4 font-semibold">{t("dashboard.upcoming")}</h3>
                            <div className="space-y-3">
                                {data.upcoming.length === 0 && <p className="text-sm text-muted-foreground">{t("dashboard.noUpcoming")}</p>}
                                {data.upcoming.map((a) => (
                                    <div key={a.id} className="flex items-center gap-3">
                                        <span className="h-9 w-1.5 rounded-full" style={{ background: a.employee_color }} />
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm font-medium">{a.customer_name}</p>
                                            <p className="truncate text-xs text-muted-foreground">
                                                {new Date(a.start).toLocaleString(lang, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} · {a.service_name}
                                            </p>
                                        </div>
                                        <span className="rounded-full px-2 py-0.5 text-xs font-medium" style={{ background: STATUS_META[a.status]?.bg, color: STATUS_META[a.status]?.color }}>{t(`status.${a.status}`)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
